import { Autocomplete, Box, Button, CircularProgress, Divider, Grid, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import SearchIcon from '@mui/icons-material/Search';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomTable from "../../components/common/CustomTable/CustomTable";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { ClientDirectionFormModal } from "./components/modals/ClientDirectionFormModal";
import { useNavigate } from "react-router-dom";
import { CustomSnackbar } from "../../components/common/CustomSnackbar/CustomSnackbar";
import { useSupplierStore } from "../../hooks/useSupplierStore";
import { ApiStatus } from "../../types/api/status";
import { ClientAdress } from "../../types/slices/clientAdressType";
import { useSearcherStore } from "../../hooks/useSearcherStore";
import { ProductSearch } from "../../types/slices/productType";
import { useShoppingCartStore } from "../../hooks/useShoppingCartStore";
import { useAuthStore } from "../../hooks/useAuthStore";
import { DetailShoppingCart, Shoppingcart } from "../../types/slices/shoppingCartType";
import { useTicketStore } from "../../hooks/useTicketStore";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { PinModal } from "./components/modals/PinModal";
import { useUserStore } from "../../hooks/UseUserStore";
import { RoutesMap } from "../../types";

interface listProduct {
    page?: number
    perPage?: number
    idcustomer?: number
    exactCode?: string
    sellingProducts?: boolean
    mostSearchedProducts?: boolean
}

export const SearcherCompany: FC = () => {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()
    const { status: statusSearcher, getSearchProducts } = useSearcherStore()
    const { status: statusShoppingCart, shoppingcart, getShoppingCart, createShoppingCart, deleteProductCart,
        clearShoppingCart, editCartProduct } = useShoppingCartStore()
    const { status: statusTicket, ticketSelected, createTicket, updateTicket } = useTicketStore()
    const { selectedCustomer, selectedAddress, setSelectedCustomer, setSelectedAddress } = useSearcherStore()
    const { verifyPIN } = useUserStore()

    const autocompleteRef = useRef<HTMLInputElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const searchRef = useRef<HTMLInputElement>(null)

    const [products, setProducts] = useState<ProductSearch[]>([])
    const [totalProducts, setTotalProducts] = useState<number>(0)
    const [addresses, setAddresses] = useState<ClientAdress[]>([])
    const [selectedShoppingCart, setSelectedShoppingCart] = useState<Shoppingcart | null>(null)
    const [detailShoppingCart, setDetailShoppingCart] = useState<DetailShoppingCart[]>([])

    // const [selectedAddress, setSelectedAddress] = useState<ClientAdress | null>(null)
    const [editPrices, setEditPrices] = useState<boolean>(false)
    const [showModalPin, setShowModalPin] = useState<boolean>(false)
    const priceInputsRef = useRef<HTMLInputElement[]>([])

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    const [isModalOpen, setModalOpen] = useState(false)
    const [showBestSellingProducts, setShowBestSellingProducts] = useState<boolean>(true)
    const [mostSearchedProducts, setMostSearchedProducts] = useState<boolean>(true)
    const [disableConfirm, setDisabledConfirm] = useState<boolean>(false)

    const isClient: boolean = user?.userType === 'Cliente'
    const isEditTicket: boolean = ticketSelected?.id ? true : false

    useEffect(() => {
        getSuppliersApi()
        if (autocompleteRef.current) {
            const input = autocompleteRef.current.querySelector('input')
            if (input) input.focus()
        }
    }, [])

    useEffect(() => {
        setSelectedShoppingCart(shoppingcart[0] || null)
        setDetailShoppingCart(shoppingcart[0]?.detail || [])
    }, [shoppingcart, supppliers])

    useEffect(() => {
        getProductsApi()
    }, [page, perPage])

    useEffect(() => {
        if (selectedCustomer) {
            setAddresses(selectedCustomer?.clientAddresses || [])
            if (!selectedAddress) {
                setSelectedAddress(selectedCustomer?.clientAddresses[0] || null)
            } else {
                const address = selectedCustomer?.clientAddresses.find(row => row.id === selectedAddress.id)
                if (!address) setSelectedAddress(null)
            }
            getProductsApi()
            getShoppingCartApi()
        } else {
            setAddresses([])
            clearShoppingCart()
        }
    }, [selectedCustomer])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                searchRef.current?.focus()
                setSearch('')
            }

            if (event.key === 'F2') {
                event.preventDefault()
                document.getElementById("btnConfirm")?.click()
            }

            if (event.key === 'F3') {
                event.preventDefault()
                if (autocompleteRef.current) {
                    const input = autocompleteRef.current.querySelector('input')
                    if (input) input.focus()
                }
            }

            if (event.key === 'F6') {
                const active = document.activeElement
                const priceInputs = priceInputsRef.current

                if (priceInputs.length === 0) return

                const index = priceInputs.findIndex(input => input === active)

                if (index === -1) {
                    event.preventDefault()
                    priceInputs[0]?.focus()
                    return
                }

                if (!event.shiftKey) {
                    event.preventDefault()
                    const next = (index + 1) % priceInputs.length
                    priceInputs[next]?.focus()
                } else {
                    event.preventDefault()
                    const prev = (index - 1 + priceInputs.length) % priceInputs.length
                    priceInputs[prev]?.focus()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [])

    useEffect(() => {
        if (isClient && supppliers.length > 0 && user?.supplier_rut) {
            const client = supppliers?.find(supplier => supplier.rut == user?.supplier_rut)
            setSelectedCustomer(client)
        }
    }, [isClient, supppliers, user])

    useEffect(() => {
        if (isEditTicket && supppliers.length > 0 && ticketSelected?.idcustomer && !selectedCustomer?.id) {
            const client = supppliers?.find(supplier => supplier.id == ticketSelected?.idcustomer)
            setSelectedCustomer(client)
        }
    }, [isEditTicket, supppliers, ticketSelected, selectedCustomer])

    const getSuppliersApi = async () => {
        if (isEditTicket) return
        await getSuppliers()
    }

    const getProductsApi = async () => {
        const payload: listProduct = {
            page: page,
            perPage: perPage
        }
        if (showBestSellingProducts === true) payload.sellingProducts = true
        if (mostSearchedProducts === true) payload.mostSearchedProducts = true
        if (selectedCustomer) payload.idcustomer = selectedCustomer.id
        if (search !== '') {
            payload.exactCode = search
            payload.sellingProducts = undefined
            payload.mostSearchedProducts = undefined
        }
        const resp: any = await getSearchProducts(payload)
        setProducts(resp.detail || [])
        setTotalProducts(resp.total || 0)
    }

    const getShoppingCartApi = async () => {
        let payload = {}
        if (ticketSelected?.id) {
            payload = {
                id: ticketSelected?.idshopping_cart
            }
        } else {
            payload = {
                iduser_created: user?.id,
                status_ticket: 0,
                idcustomer: selectedCustomer?.id || undefined
            }
        }
        await getShoppingCart(payload)
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const handleSelectProduct = async (
        idproduct?: number,
        price?: number,
        quantity?: number,
        code_system?: null | number | string
    ): Promise<boolean> => {
        let payload: any = {
            iduser_created: user.id,
            idproduct,
            price,
            quantity: quantity ?? 1,
            code_system
        }

        if (selectedCustomer) payload = { ...payload, idcustomer: selectedCustomer.id }
        if (ticketSelected?.id) payload = { ...payload, id: ticketSelected?.idshopping_cart }

        const response = await createShoppingCart(payload)

        if (response === true) {
            if (inputRef.current) {
                const input = inputRef.current.querySelector('input')
                if (input) {
                    input.focus()
                    input.select()
                }
            }
        }

        return response
    }

    const handleDeleteItem = async (iddetail: number) => {
        await deleteProductCart(iddetail)
    }

    const handleChangeQuantity = (iddetail: number, value: string) => {
        const newQuantity = value

        setDetailShoppingCart((prevDetailShoppingCart) => {
            return prevDetailShoppingCart.map((detail) => {
                if (detail.id === iddetail) {
                    return { ...detail, quantity: newQuantity }
                }
                return detail
            })
        })
    }

    const handleChangePrice = (iddetail: number, value: string): void => {
        const newPrice = value

        if (!editPrices) {
            setShowModalPin(true)

            return
        }

        setDetailShoppingCart((prevDetailShoppingCart) => {
            return prevDetailShoppingCart.map((detail) => {
                if (detail.id === iddetail) {
                    return { ...detail, price: newPrice }
                }
                return detail
            })
        })
    }

    const onBlurQuantity = async (iddetail: number, value: string) => {
        const response = await editCartProduct(iddetail, { quantity: value })
        if (response === true) {
            if (disableConfirm) setDisabledConfirm(false)
        }
    }

    const onBlurPrice = async (iddetail: number, value: string) => {
        const response = await editCartProduct(iddetail, { price: value })
        if (response === true) {
            if (disableConfirm) setDisabledConfirm(false)
        }
    }

    const onSubmitPin = async (pin: string) => {
        const resp = await verifyPIN({
            iduser: user?.id,
            pin
        })

        if (resp === true) {
            setEditPrices(true)
            setShowModalPin(false)
        }
    }

    const handleOpenRegisterAddressModal = () => {
        if (!selectedCustomer) return CustomSnackbar('warning', 'Seleccione un cliente')
        setModalOpen(true)
    }

    const onAddShoppingCart = async (rowSelected?: any, code_system?: number | string): Promise<boolean> => {
        return await handleSelectProduct(rowSelected?.idproduct, rowSelected?.price_product, 1, code_system)
    }

    const onDeleteShoppingCart = (rowSelected) => {
        handleDeleteItem(rowSelected?.id)
    }

    const handleSearch = async (event): Promise<void> => {
        if (event.key === 'Enter') {
            if (search === '') {
                return
            }

            const resp = await onAddShoppingCart(null, search)

            if (resp === true) {
                setSearch('')

                if (inputRef.current) {
                    const input = inputRef.current.querySelector('input')
                    if (input) input.focus()
                }
            } else {
                setSearch(search)
                getProductsApi()
            }
        } else {
            if (search === '') {
                getProductsApi()
            }
        }
    }

    const onCreateTicket = async () => {
        if (!selectedCustomer) return CustomSnackbar('warning', 'Seleccione un cliente.')
        if (!selectedAddress) return CustomSnackbar('warning', 'Seleccione una dirección.')

        if (!ticketSelected?.id) {
            const payload: any = {
                idshopping_cart: selectedShoppingCart?.id,
                idtype_document: 2,
                idway_to_pay: selectedCustomer?.idwaytopay,
                customer_rut: selectedCustomer?.rut,
                customer_name: selectedCustomer?.name_rz,
                bussines_area: selectedCustomer?.iderp_business_area,
                idclient_address: selectedAddress?.id,
                idsalesman: selectedCustomer?.idsalesman,
                status: 0
            }

            const response = await createTicket(payload)

            if (response?.id) {
                if (!isClient) {
                    navigate(`/preview-document/${response.id}`)
                } else {
                    navigate(`/client-preview-ticket/${response.id}`)
                }
            }
        } else {
            const payload = {
                idshopping_cart: selectedShoppingCart?.id,
                idticket: ticketSelected?.id
            }
            const resp = await updateTicket(payload)
            if (resp === true) {
                navigate(RoutesMap.MY_ORDERS)
            }
        }
    }

    const columns = [
        { type: 'text', field: 'codesystem_product', label: 'Código', align: 'center' },
        { type: 'text', field: 'name_product', label: 'Nombre', align: 'center' },
        { type: 'text', field: 'unitmeasure_product.abbreviation', label: 'Unid. Med.', align: 'center', format: (row) => row?.unitmeasure_product?.abbreviation },
        { type: 'text', field: 'price_product', label: 'Precio Unit.', align: 'right', format: (row) => moneyCL(row.price_product) },
        { type: 'detail', field: 'detail', label: 'Detalle', align: 'center' },
    ]

    return (
        <Grid container spacing={2}>
            {/* Buscador y Productos */}
            <Grid item container xs={12} sm={12} md={8} lg={8} xl={7} spacing={2}>
                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            padding: '15px',
                            backgroundColor: '#F9F9F9',
                        }}
                    >
                        {!isClient && (
                            <Autocomplete
                                ref={autocompleteRef}
                                value={selectedCustomer}
                                options={supppliers || []}
                                onChange={(e, value) => {
                                    setSelectedCustomer(value)
                                }}
                                getOptionLabel={(option) => option.name_rz + ' - ' + option.rut}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Buscar cliente..."
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{ color: '#516E80' }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            style: {
                                                borderRadius: '8px',
                                            },
                                        }}
                                        sx={{
                                            width: '100%',
                                            backgroundColor: '#fff',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#E0E0E0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#516E80',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#516E80',
                                                },
                                            },
                                        }}
                                    />
                                )}
                                size="small"
                                loading={statusSupplier === ApiStatus.FETCHING}
                                readOnly={isClient || isEditTicket}
                            />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '15px' }}>
                            <TextField
                                select
                                size="small"
                                variant="outlined"
                                value={selectedAddress?.id || ''}
                                onChange={(e) => {
                                    const selected = addresses.find((row) => row.id === parseInt(e.target.value))
                                    setSelectedAddress(selected || null)
                                }}
                                sx={{
                                    flex: 1,
                                    backgroundColor: '#fff',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#E0E0E0',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#516E80',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#516E80',
                                        },
                                    },
                                }}
                            >
                                {addresses.map((address) => (
                                    <MenuItem key={address.id} value={address.id}>
                                        {address.address + ' - ' + address.district}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleOpenRegisterAddressModal}
                                sx={{
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    padding: '6px 12px',
                                    minWidth: '40px',
                                    backgroundColor: '#91CBF9',
                                    color: '#fff',
                                    "&:hover": {
                                        backgroundColor: "#73B0E6",
                                    },
                                }}
                            >
                                +
                            </Button>
                        </Box>
                    </div>
                </Grid>

                {/** LISTA DE PRODUCTOS */}
                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            padding: '15px',
                            backgroundColor: '#F9F9F9',
                            minHeight: '500px'
                        }}
                    >
                        <TextField
                            inputRef={searchRef}
                            variant="outlined"
                            placeholder="Buscar producto..."
                            size="small"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value)
                            }}
                            onKeyDown={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#516E80' }} />
                                    </InputAdornment>
                                ),
                                style: {
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#E0E0E0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#516E80',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#516E80',
                                    },
                                },
                            }}
                        />
                        <CustomTable
                            columns={columns}
                            loading={statusSearcher === ApiStatus.FETCHING}
                            rows={products || []}
                            onRowClick={() => { }}
                            hasOptions
                            dense
                            onHelper={onAddShoppingCart}
                            tooltip_helper={"Agregar al carrito"}
                            icon_helper={<AddShoppingCartIcon fontSize="small" />}

                            totalRows={totalProducts}
                            rowsPerPage={perPage}
                            page={page}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            withCustomPagination={true}
                        />
                    </div>
                </Grid>
            </Grid >

            {/* Carrito y Totales */}
            <Grid item container xs={12} sm={12} md={4} lg={4} xl={4.99} spacing={2} >
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>
                        Artículos de la cesta ({detailShoppingCart.length})
                    </Typography>
                    <Divider />
                </Grid>
                <Grid item xs={12} sx={{ height: '60%' }}>
                    <Grid container spacing={2}
                        sx={{
                            height: '100%',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            maxHeight: '475px'
                        }}
                    >
                        {detailShoppingCart.length > 0 ? (
                            detailShoppingCart.map((element, index) => (
                                <Grid item xs={12} key={index}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            border: '1px solid #E0E0E0',
                                            borderRadius: '8px',
                                            padding: '15px',
                                            backgroundColor: '#F9F9F9',
                                            position: 'relative',
                                            gap: '15px',
                                        }}
                                    >


                                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 'bold', color: '#333', marginBottom: '5px' }}
                                            >
                                                {element.name_product}
                                            </Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {user?.edit_price == '1' ? (
                                                    <TextField
                                                        key={element.id}
                                                        type="text"
                                                        size="small"
                                                        value={element?.price}
                                                        onChange={(e) => {
                                                            const v = e.target.value
                                                            if (/^\d*$/.test(v) || v === "") {
                                                                handleChangePrice(element.id, v)
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End']
                                                            const metaCombo = (e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())
                                                            if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key) && !metaCombo) {
                                                                e.preventDefault()
                                                            }
                                                        }}
                                                        onBlur={(e) => {
                                                            const raw = e.target.value
                                                            const min = Number(element.cost_product)

                                                            if (raw === "") {
                                                                onBlurPrice(element.id, "")
                                                                return
                                                            }

                                                            const n = Number(raw)
                                                            const corrected = n < min ? String(min) : String(n)

                                                            if (corrected !== raw) {
                                                                CustomSnackbar('warning', 'El precio es menor al costo del producto')
                                                                handleChangePrice(element.id, corrected)
                                                            }
                                                            onBlurPrice(element.id, corrected)
                                                        }}
                                                        inputRef={(ref) => { if (ref) priceInputsRef.current[index] = ref }}
                                                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <span>$</span>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            width: '120px',
                                                            "& .MuiInputBase-root": { fontSize: '14px' },
                                                            fontWeight: 'bold', color: '#516E80'
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ fontWeight: 'bold', color: '#516E80' }}
                                                    >
                                                        {moneyCL(element.price)}
                                                    </Typography>
                                                )}
                                            </div>
                                        </div>

                                        {/* Cantidad */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <Typography variant="body2" sx={{ color: '#555' }}>
                                                Cantidad:
                                            </Typography>
                                            <TextField
                                                ref={inputRef}
                                                id={"quantity_" + element.idproduct}
                                                type="text"
                                                name={"quantity_" + element.idproduct}
                                                size="small"
                                                value={element?.quantity}
                                                onChange={(e) => {
                                                    const { value } = e.target

                                                    const isValid =
                                                        element?.idunitmeasure_product === 1
                                                            ? /^\d+$/.test(value)
                                                            : /^\d*\.?\d{0,2}$/.test(value)

                                                    if (isValid || value === "") {
                                                        handleChangeQuantity(element?.id, value)
                                                    }
                                                }}
                                                onFocus={() => setDisabledConfirm(true)}
                                                onBlur={() => {
                                                    onBlurQuantity(element?.id, element?.quantity)
                                                }}
                                                inputProps={{
                                                    min: 1,
                                                    inputMode: "decimal",
                                                    pattern: "[0-9]*",
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>{element?.unitmeasure_abbreviation || ''}</span>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: '120px',
                                                    "& .MuiInputBase-root": { fontSize: '14px' },
                                                }}
                                            />
                                        </div>

                                        {/* Botón Eliminar */}
                                        <IconButton
                                            sx={{
                                                backgroundColor: '#FF6666',
                                                color: '#FFF',
                                                "&:hover": { backgroundColor: '#E05555' },
                                            }}
                                            onClick={() => onDeleteShoppingCart(element)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        backgroundColor: '#F9F9F9',
                                        height: '100%'
                                    }}
                                >
                                    <ProductionQuantityLimitsIcon
                                        sx={{ fontSize: '50px', color: '#9C9C9C', marginBottom: '10px' }}
                                    />
                                    <Typography sx={{ color: '#3f3f3f', fontSize: '16px' }}>
                                        Aún no tienes productos en tu canasta
                                    </Typography>
                                </div>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Totales */}
                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            padding: '15px',
                            backgroundColor: '#F9F9F9',
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>
                                        Total neto:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#516E80' }}>
                                        {moneyCL(selectedShoppingCart?.net_total || 0)}
                                    </Typography>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography sx={{ color: '#333' }}>
                                        IVA (19%):
                                    </Typography>
                                    <Typography sx={{ fontWeight: 'bold', color: '#516E80' }}>
                                        {moneyCL(selectedShoppingCart?.IGV || 0)}
                                    </Typography>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>
                                        Total bruto:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#516E80' }}>
                                        {moneyCL(selectedShoppingCart?.total || 0)}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    </div>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        id="btnConfirm"
                        sx={{
                            borderRadius: '20px',
                            fontSize: '12px',
                            backgroundColor: '#91CBF9',
                            color: '#fff',
                            width: '100%',
                            padding: '6px 12px',
                            "&:hover": {
                                backgroundColor: "#73B0E6",
                            },
                        }}
                        disabled={statusShoppingCart === ApiStatus.FETCHING || disableConfirm}
                        onClick={onCreateTicket}
                    >
                        {statusShoppingCart === ApiStatus.FETCHING && <CircularProgress color="inherit" size={20} />}
                        Confirmar (F2)
                    </Button>
                </Grid>
            </Grid>

            {isModalOpen && (
                <ClientDirectionFormModal
                    open={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    selectedClient={selectedCustomer}
                    addresses={addresses}
                    setAddresses={setAddresses}
                />
            )}

            {showModalPin && (
                <PinModal
                    open={showModalPin}
                    closeModal={() => setShowModalPin(false)}
                    onSubmitPin={onSubmitPin}
                />
            )}
        </Grid >
    )
}